import { resolveLang } from '@/i18n'
import { APP_VERSION } from '@/lib/version'

/**
 * A light record of what went wrong in this tab, attached to bug reports.
 *
 * Only the last MAX_ENTRIES of each kind are kept, and request URLs lose their
 * query string: searches and tokens travel there.
 */

export const MAX_ENTRIES = 20
const MESSAGE_MAX = 500
const PATH_MAX = 500
const STACK_MAX = 2000
/** Serialized size ceiling, below the backend's 64 KB limit on the context. */
export const CONTEXT_MAX_BYTES = 60 * 1024

export interface ConsoleError {
  message: string
  stack?: string
  at: string
}

export interface FailedRequest {
  method: string
  path: string
  status: number
  request_id: string | null
  at: string
}

export interface DiagnosticContext {
  user_agent: string
  viewport: string
  app_version: string
  locale: string
  console_errors: ConsoleError[]
  failed_requests: FailedRequest[]
}

let consoleErrors: ConsoleError[] = []
let failedRequests: FailedRequest[] = []

function push<T>(buffer: T[], entry: T): T[] {
  return [...buffer, entry].slice(-MAX_ENTRIES)
}

/**
 * ``text`` cut to at most ``max`` UTF-16 code units without splitting a
 * surrogate pair: a plain slice() can keep half an emoji, a lone surrogate
 * that is not valid Unicode.
 */
function truncate(text: string, max: number): string {
  if (text.length <= max) return text
  const cut = text.slice(0, max)
  const last = cut.charCodeAt(cut.length - 1)
  // A high surrogate at the end lost its low half to the cut.
  return last >= 0xd800 && last <= 0xdbff ? cut.slice(0, -1) : cut
}

export function stripQuery(url: string): string {
  return url.split(/[?#]/, 1)[0]
}

export function recordError(message: string, stack?: string): void {
  consoleErrors = push(consoleErrors, {
    message: truncate(message, MESSAGE_MAX),
    ...(stack ? { stack: truncate(stack, STACK_MAX) } : {}),
    at: new Date().toISOString(),
  })
}

export function recordFailedRequest(request: {
  method: string
  url: string
  status: number
  requestId: string | null
}): void {
  failedRequests = push(failedRequests, {
    method: request.method.toUpperCase(),
    path: truncate(stripQuery(request.url), PATH_MAX),
    status: request.status,
    request_id: request.requestId,
    at: new Date().toISOString(),
  })
}

function describeReason(reason: unknown): { message: string; stack?: string } {
  if (reason instanceof Error) return { message: reason.message, stack: reason.stack }
  return { message: String(reason) }
}

/** Listen for uncaught errors and rejections; returns the uninstaller. */
export function installDiagnostics(target: Window = window): () => void {
  const onError = (event: ErrorEvent) => {
    const { stack } = describeReason(event.error)
    recordError(event.message || describeReason(event.error).message, stack)
  }
  const onRejection = (event: PromiseRejectionEvent) => {
    const { message, stack } = describeReason(event.reason)
    recordError(message, stack)
  }
  target.addEventListener('error', onError)
  target.addEventListener('unhandledrejection', onRejection)
  return () => {
    target.removeEventListener('error', onError)
    target.removeEventListener('unhandledrejection', onRejection)
  }
}

function serializedBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length
}

/** Drop the oldest entry of either kind; ISO timestamps sort as strings. */
function dropOldest(context: DiagnosticContext): void {
  const [error] = context.console_errors
  const [request] = context.failed_requests
  if (error && (!request || error.at <= request.at)) context.console_errors.shift()
  else context.failed_requests.shift()
}

export function snapshotDiagnostics(): DiagnosticContext {
  const context: DiagnosticContext = {
    user_agent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    app_version: APP_VERSION,
    locale: resolveLang(),
    console_errors: [...consoleErrors],
    failed_requests: [...failedRequests],
  }
  // The per-entry caps count characters, not bytes: accented text, or quotes
  // and newlines that JSON escapes, can push full buffers past the backend
  // limit, and every report would then be rejected until a reload.
  while (
    serializedBytes(context) > CONTEXT_MAX_BYTES &&
    context.console_errors.length + context.failed_requests.length > 0
  ) {
    dropOldest(context)
  }
  return context
}

export function resetDiagnostics(): void {
  consoleErrors = []
  failedRequests = []
}
